import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Stack,
  Button,
  Typography,
  Grid,
  Card,
  Paper,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Pagination,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  DialogActions,
  DialogContentText,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import CallMade from '@mui/icons-material/CallMade';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import ReactApexChart from 'react-apexcharts';
import Iconify from 'src/components/iconify';
import moment from 'moment';
import Services from 'src/services/Services';
import { useSelector } from 'react-redux';
import api from 'src/services/BaseService';
import { toast } from 'react-toastify';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';

export default function MallManagementCorrespondence() {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const customer = useSelector((state) => state.customerReducer);
  const mallID = customer?.customer?.id;
  const theme = useTheme();

  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);

  const [rowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // GÜNCELLENDİ: Yeni tablo yapısı için state'ler
  const [muhaberatList, setMuhaberatList] = useState([]);
  const [originalMuhaberatList, setOriginalMuhaberatList] = useState([]);

  const [isVideoModalOpen, setVideoModalOpen] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);

  // Check service permission using hook
  const { hasPermission: correspondencePermission, loading: permissionLoading } = useServicePermission(SERVICES.CORRESPONDENCE);

  const handleOpenVideoModal = () => setVideoModalOpen(true);
  const handleCloseVideoModal = () => setVideoModalOpen(false);

  const handleApplyClick = () => setOpenApplyDialog(true);
  const handleCloseDialog = () => setOpenApplyDialog(false);
  const handleConfirmApply = async () => {
    try {
      const mallsResponse = await api.post('/malls/get-malls', { withOffices: true });

      if (mallsResponse.data && mallsResponse.data.status) {
        const foundMall = mallsResponse.data.result.find((item) => item.id === mallID);
        const mallTitle = foundMall?.title || 'Bilinmeyen AVM';

        const formData = {
          mallName: mallTitle,
          mallID: mallID,
          userName: parsedUser?.name || parsedUser?.email || 'Bilinmeyen Kullanıcı',
          serviceName: 'Muhaberat',
        };

        const response = await Services.createContactForm(parsedUser.token, formData);

        if (response.data && response.data.status) {
          toast.success('Talebiniz başarıyla iletilmiştir.');
          handleCloseDialog();
        } else {
          toast.error(response.data?.message || 'Talep gönderilirken bir hata oluştu.');
        }
      } else {
        toast.error('AVM bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Talep gönderilirken bir hata oluştu.');
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      if (permissionLoading) {
        return;
      }

      if (!mallID) {
        setLoading(false);
        return;
      }
      if (!mallID) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const userHasPermission = correspondencePermission;
        setHasPermission(userHasPermission);

        if (userHasPermission) {
          const analyticsResponse = await Services.GetLiveAnalyticsMallMuhaberat(parsedUser.token, mallID);
          if (analyticsResponse.data && analyticsResponse.data.result) {
            setAnalyticsData(analyticsResponse.data.result);
          } else {
            setAnalyticsData({});
          }

          const statusListParams = [1, 2, 3, 4, 5, 6, 7]; // VEYA: StatusList.filter(x => x.id != 0).map(x => x.id)

          const tableResponse = await api.post('posts/get', {
            type: 'post-office',
            mallID: mallID,
            status: statusListParams,
            limit: 2000,
            direction: ['CustomerToMallLogistics', 'MallLogisticsToCustomer']
          });

          if (tableResponse.data && tableResponse.data.status) {
            const list = tableResponse.data.result || [];
            setMuhaberatList(list);
            setOriginalMuhaberatList(list);
          } else {
            setMuhaberatList([]);
            setOriginalMuhaberatList([]);
          }
        } else {
          setAnalyticsData({});
          setShowApplyBanner(true);
        }
      } catch (error) {
        console.error("Veri yüklenirken hata oluştu:", error);
        setAnalyticsData({});
        setMuhaberatList([]);
        setOriginalMuhaberatList([]);
        toast.error("Veriler yüklenirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [mallID, parsedUser?.token, correspondencePermission, permissionLoading]);

  const handleFilter = async () => {
    if (!startDate || !endDate) {
      toast.error('Lütfen başlangıç ve bitiş tarihlerini seçin.');
      return;
    }
    setIsFiltering(true);

    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');

    try {
      const statusListParams = [1, 2, 3, 4, 5, 6, 7]; // Yukarıdaki ile aynı olmalı

      const response = await api.post('posts/get', {
        type: 'post-office',
        mallID: mallID,
        status: statusListParams,
        limit: 2000,
        direction: ['CustomerToMallLogistics', 'MallLogisticsToCustomer'],
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });

      if (response.data && response.data.status) {
        setMuhaberatList(response.data.result || []);
      } else {
        setMuhaberatList([]);
        toast.info(response.data?.message || 'Seçilen aralıkta veri bulunamadı.');
      }
      setPage(1);
    } catch (error) {
      console.error("Filtreli veri çekilirken hata oluştu:", error);
      toast.error("Veri çekilirken bir hata oluştu.");
    } finally {
      setIsFiltering(false);
    }
  };

  // GÜNCELLENDİ: Temizleme fonksiyonu API çağrısı yapmayacak şekilde düzenlendi.
  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setMuhaberatList(originalMuhaberatList);
    setPage(1);
  };

  const paginatedRows = muhaberatList.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const { monthlyChart, weeklyChart, cargoChart, postStasuses } = useMemo(() => {
    const data = analyticsData || {};
    const commonBarPlot = { bar: { columnWidth: '20%', borderRadius: 8, borderRadiusApplication: 'end' } };
    const weeklyBarPlot = { bar: { columnWidth: '40%', borderRadius: 8, borderRadiusApplication: 'end' } };

    const buildMonthlyChart = (monthlyData) => {
      if (!monthlyData || !monthlyData.inbound) return null;
      const { inbound, outbound } = monthlyData;
      const correctOrder = ["1-7", "8-14", "15-21", "22-rest"];
      const categories = correctOrder.map(key => key === '22-rest' ? `22-${moment().endOf('month').date()}` : key);
      const inboundData = correctOrder.map(key => Number(inbound[key] || 0));
      const outboundData = correctOrder.map(key => Number(outbound[key] || 0));

      return {
        options: { chart: { type: 'bar', stacked: true, toolbar: { show: false } }, plotOptions: commonBarPlot, stroke: { show: true, width: 2, colors: ['#fff'] }, colors: ['#1C64F2', '#FDBA8C'], legend: { position: 'bottom' }, xaxis: { categories }, yaxis: { title: { text: 'Toplam Adet' } }, dataLabels: { enabled: false } },
        series: [{ name: 'İçeri Giren Paket', data: inboundData }, { name: 'Dışarı Çıkan Paket', data: outboundData }],
      };
    };

    const buildWeeklyChart = (weeklyData) => {
      if (!weeklyData || !weeklyData.inbound) return null;
      const startOfWeek = moment().startOf('isoWeek');
      const days = [...Array(7)].map((_, i) => startOfWeek.clone().add(i, 'days'));
      const rawDates = days.map(d => d.format('YYYY-MM-DD'));
      const categories = days.map(d => d.format('MMM DD'));
      const inboundData = rawDates.map(date => Number(weeklyData.inbound[date] || 0));
      const outboundData = rawDates.map(date => Number(weeklyData.outbound[date] || 0));
      return {
        options: { chart: { type: 'bar', stacked: true, toolbar: { show: false } }, plotOptions: weeklyBarPlot, stroke: { show: true, width: 2, colors: ['#fff'] }, colors: ['#1C64F2', '#FDBA8C'], legend: { position: 'bottom' }, xaxis: { categories }, yaxis: { title: { text: 'Toplam Adet' } }, dataLabels: { enabled: false } },
        series: [{ name: 'İçeri Giren Paket', data: inboundData }, { name: 'Dışarı Çıkan Paket', data: outboundData }],
      };
    };

    const buildDonutChart = (cargoData) => {
      if (!cargoData || cargoData.length === 0) return null;

      const predefinedColors = ['#3150A0', '#65A6A0', '#F7A937', '#E63A8A', '#7E57C2', '#F44336'];

      // Veriyi sıralıyoruz
      const sortedData = [...cargoData]
        .map(item => ({ shipment_firm: item.shipment_firm, usage_count: Number(item.usage_count) }))
        .sort((a, b) => b.usage_count - a.usage_count);
      const processedData = sortedData;

      const totalUsage = sortedData.reduce((acc, item) => acc + item.usage_count, 0);

      const labels = processedData.map(item => item.shipment_firm);
      const series = processedData.map(item => totalUsage > 0 ? parseFloat(((item.usage_count / totalUsage) * 100).toFixed(1)) : 0);
      const colors = processedData.map((item, i) => predefinedColors[i % predefinedColors.length]);

      return {
        options: {
          chart: {
            type: 'donut',
            toolbar: { show: false }
          },
          labels,
          colors,
          legend: { position: 'bottom' },
          dataLabels: { enabled: false },
          tooltip: {
            y: { formatter: (val) => `${val}%` }
          },
          plotOptions: {
            pie: {
              donut: {
                size: '65%',
                labels: {
                  show: true,
                  name: { show: true },
                  value: {
                    show: true,
                    formatter: (val) => `% ${val}`
                  },
                  total: { show: false }
                }
              }
            }
          }
        },
        series,
      };
    };

    return {
      monthlyChart: buildMonthlyChart(data.monthlyMuhaberatTransactions),
      weeklyChart: buildWeeklyChart(data.weeklyMuhaberatTransactions),
      cargoChart: buildDonutChart(data.monthlyMuhaberatCargoFirmsUsage),
      postStasuses: data.postStatuses,
    };
  }, [analyticsData]);

  const zeroOutSeries = (series) => {
    if (!Array.isArray(series)) return [];
    const firstEl = series[0];
    if (typeof firstEl === 'number') { return series.map(() => 0); }
    if (typeof firstEl === 'object' && firstEl !== null && 'data' in firstEl) {
      return series.map(s => ({ ...s, data: Array.isArray(s.data) ? s.data.map(() => 0) : [] }));
    }
    return [];
  };

  const donut2Series = [25.6, 28.4, 22.8, 23.2];
  const donut2Options = {
    chart: { type: 'donut', toolbar: { show: false } },
    labels: ['Ofis', 'Rezidans', 'Mağaza', 'Bireysel'],
    colors: ['#3150A0', '#65A6A0', '#F7A937', '#E63A8A'],
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: { show: true, fontSize: '15px', color: '#6B7280', fontWeight: '400', offsetY: 25, formatter: (val) => val },
            value: { show: true, fontSize: '30px', fontWeight: '700', offsetY: -12, color: '#111', formatter: (val) => `% ${val}` },
            total: { show: false }
          }
        }
      }
    },
    legend: { position: 'bottom', horizontalAlign: 'center' },
    dataLabels: { enabled: false },
    tooltip: { shared: false },
  };

  const stats = [
    { title: 'Şubede Bekleyen Paket Sayısı', value: postStasuses?.waiting ?? 0, gradient: 'linear-gradient(135deg,#F7A937 0%,#DC9328 100%)' },
  ];

  function exportToExcel() {
    const dataToExport = muhaberatList.map((item, index) => ({
      'No': index + 1,
      'Barkod': item.barcode || '-',
      'Kişi': item.dataJSON?.customerName || '-',
      'Firma': item.branchTitle || '-',
      'Kule': item.dataJSON?.tower || '-',
      'Daire': item.dataJSON?.office || '-',
      'Firma ID': item.customer?.corporationID || '-',
      'Kargo Durumu': item.status === '5' ? 'Şubede' : 'Teslim Edildi',
      'Paket Sayısı': item.orderInventory || '-',
      'Teslim Alan Kişi': item.handover ? `${item.handover.name} ${item.handover.lastname}` : '-',
      'Oluşturulma Tarihi': moment.unix(item.createdDateTime).format('DD/MM/YYYY HH:mm'),
      'Teslim Tarihi': item.deliveredDateTime ? moment.unix(item.deliveredDateTime).format('DD/MM/YYYY HH:mm') : '-',
      'Kargo Firması': item.shipmentFirm || '-',
    }));

    api.post('excel/export', {
      type: 'excel',
      content: dataToExport,
      sheetTitle: 'Muhaberat İşlemleri'
    })
      .then(response => {
        if (response.data.status) {
          window.open(response.data.result, '_blank');
        } else {
          toast.error(response.data.message);
        }
      }).catch(error => toast.error(error.message));
  }

  const maskName = (name) => {
    if (!name) {
      return '-';
    }

    const len = name.length;

    if (len <= 2) {
      return name;
    }

    if (len === 3) {
      return `${name.slice(0, 1)}*${name.slice(-1)}`;
    }

    if (len === 4) {
      return `${name.slice(0, 1)}**${name.slice(-1)}`;
    }
    return `${name.slice(0, 2)}${'*'.repeat(len - 4)}${name.slice(-2)}`;
  };

  if (loading || permissionLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
  }


  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: 3 }}>
      <Helmet><title>Muhaberat</title></Helmet>

      {showApplyBanner && !hasPermission && (
        <Stack sx={{ width: '100%', mb: 4, mx: 'auto', borderBottom: '1px solid #D6BBFB', borderRadius: 1, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Alert severity="info" sx={{ backgroundColor: 'white', alignItems: 'center', pr: 4, '& .MuiAlert-icon': { color: theme.palette.info.main } }} action={
            <Stack direction="row" gap={1} alignItems="center">
              <Button color="primary" variant="contained" size="small" onClick={handleApplyClick} sx={{ padding: '10px 16px', cursor: 'pointer' }}>Başvur</Button>
              <IconButton aria-label="close" color="inherit" size="small" sx={{ cursor: 'pointer' }} onClick={() => { setShowApplyBanner(false); }}><Iconify icon="eva:close-fill" width={20} height={20} /></IconButton>
            </Stack>
          } onClose={() => setShowApplyBanner(false)} >
            Bu hizmet şu anda aktif değil. Grafik ve liste verilerini görüntülemek için başvuruda bulunabilirsiniz.
          </Alert>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Muhaberat</Typography>        
      </Stack>

      <Grid container spacing={2}>
        {[
          { title: 'Haftalık Muhaberat Hareketleri', subtitle: 'Toplam Adet', chartData: monthlyChart, type: 'bar' },
          { title: 'Aylık Muhaberat Hizmeti Kullanımı', subtitle: 'Kargo Firması Tercihine Göre Dağılımı', chartData: cargoChart, type: 'donut' },
          { title: 'Günlük Muhaberat Hareketleri', subtitle: 'Toplam Adet', chartData: weeklyChart, type: 'bar' },
          { title: 'Haftalık Muhaberat Hizmeti Kullanımı', subtitle: 'Teslimat Tercihine Göre Dağılımı', chartData: { options: donut2Options, series: donut2Series }, type: 'donut' },
        ].map((cfg, i) => (
          <Grid item xs={12} md={6} key={i}>
            <Card sx={{ position: 'relative', height: 360, p: 2, bgcolor: 'background.paper' }} elevation={0}>
              <Typography variant="h6" gutterBottom>{cfg.title}</Typography>
              <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>{cfg.subtitle}</Typography>
              {cfg.chartData ? (
                <ReactApexChart options={cfg.chartData.options} series={hasPermission ? cfg.chartData.series : zeroOutSeries(cfg.chartData.series)} type={cfg.type} height={260} />
              ) : (<Box display="flex" justifyContent="center" alignItems="center" height={260}><Typography variant="body2" color="text.secondary">Veri yok</Typography></Box>)}
            </Card>
          </Grid>
        ))}
      </Grid>
      {/*
      <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }} elevation={0}>
        <Grid container spacing={1}>
          {stats.map((s, i) => (
            <Grid item xs={12} md={12} key={i}>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Box sx={{ width: 56, height: 56, borderRadius: 2, background: s.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                  <CallMade sx={{ color: '#fff', fontSize: 24 }} />
                </Box>
                <Typography variant="subtitle2" color="text.secondary">{s.title}</Typography>
                <Typography variant="h4" sx={{ mt: 1, fontWeight: 600 }}>{s.value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
       */}

      <Paper sx={{ mt: 4, p: 3, bgcolor: 'background.paper' }} elevation={0}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} md={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Başlangıç Tarihi" value={startDate} onChange={setStartDate} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={6} md={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Bitiş Tarihi" value={endDate} onChange={setEndDate} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={4} md={1} marginLeft={'auto'}><Button fullWidth variant="contained" size="medium" onClick={handleFilter} disabled={isFiltering}>{isFiltering ? <CircularProgress size={24} color="inherit" /> : 'FİLTRELE'}</Button></Grid>
          <Grid item xs={4} md={1}><Button onClick={handleClearFilters} fullWidth variant="outlined" color="secondary" disabled={isFiltering}>TEMİZLE</Button></Grid>
          <Grid item xs={4} md={2}><Button onClick={exportToExcel} fullWidth variant="contained" color="info" size="medium">EXCEL'E AKTAR</Button></Grid>
        </Grid>

        {/* GÜNCELLENDİ: TABLO YAPISI BAŞTAN AŞAĞI YENİLENDİ */}
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 3, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'background.paper' }}>
              <TableRow>
                {['No', 'Barkod', 'Kişi', 'Firma', 'Kule', 'Daire', 'Firma ID', 'Kargo Durumu', 'Paket Sayısı', 'Teslim Alan Kişi', 'Oluşturulma Tarihi', 'Teslim Tarihi', 'Kargo Firması'].map((headCell) => (
                  <TableCell key={headCell} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{headCell}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.length > 0 ? paginatedRows.map((row, index) => (
                <TableRow key={row.id} hover>
                  <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{row.barcode || '-'}</TableCell>
                  <TableCell>{maskName(row.dataJSON?.customerName)}</TableCell>
                  <TableCell>{row.branchTitle || '-'}</TableCell>
                  <TableCell>{row.dataJSON?.tower || '-'}</TableCell>
                  <TableCell>{row.dataJSON?.office || '-'}</TableCell>
                  <TableCell>{row.customer?.corporationID || '-'}</TableCell>
                  <TableCell>
                    <Box component="span" sx={{
                      px: 1.5, py: 0.5, borderRadius: '20px', border: '1px solid',
                      borderColor: row.status === '1' ? '#BBF7D0' : '#FDE68A',
                      bgcolor: row.status === '1' ? '#F0FDF4' : '#FFFBEB',
                      color: row.status === '1' ? '#15803D' : '#B45309',
                      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap'
                    }}>
                      {row.status === '1' ? 'Teslim Edildi' : 'Şubede'}
                    </Box>
                  </TableCell>
                  <TableCell>{row.orderInventory || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {maskName(row.handover ? `${row.handover.name} ${row.handover.lastname}` : null)}
                  </TableCell>
                  <TableCell>{moment.unix(row.createdDateTime).format('DD/MM/YYYY HH:mm')}</TableCell>
                  <TableCell>{row.deliveredDateTime ? moment.unix(row.deliveredDateTime).format('DD/MM/YYYY HH:mm') : '-'}</TableCell>
                  <TableCell>{row.shipmentFirm || '-'}</TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    Veri bulunamadı.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={13} sx={{ py: 2 }}>
                  <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item>
                      <Typography variant="caption">{`Toplam ${muhaberatList.length} kayıt`}</Typography>
                    </Grid>
                    {Math.ceil(muhaberatList.length / rowsPerPage) > 1 && (
                      <Grid item>
                        <Pagination count={Math.ceil(muhaberatList.length / rowsPerPage)} page={page} onChange={(e, val) => setPage(val)} shape="rounded" size="small" />
                      </Grid>
                    )}
                  </Grid>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={isVideoModalOpen} onClose={handleCloseVideoModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>Tanıtım Videosu<IconButton aria-label="close" onClick={handleCloseVideoModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent dividers><video width="100%" height="auto" controls autoPlay src="https://files.easypoint.com.tr/files/data-ekranlari/ml.mp4">Tarayıcınız video etiketini desteklemiyor.</video></DialogContent>
      </Dialog>

      <Dialog open={openApplyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hizmet Başvurusu</DialogTitle>
        <DialogContent><DialogContentText>Bu hizmetle ilgili bilgi alma isteğiniz Genel Merkezimize iletilecektir. Onaylıyor musunuz?</DialogContentText></DialogContent>
        <DialogActions><Button onClick={handleCloseDialog}>İptal</Button><Button onClick={handleConfirmApply} autoFocus>Onayla</Button></DialogActions>
      </Dialog>
    </Box>
  );
}